import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import AppsIcon from '@atlaskit/icon/core/apps';
import GlobeIcon from '@atlaskit/icon/core/globe';
import OfficeBuildingIcon from '@atlaskit/icon/core/office-building';
import Image from '@atlaskit/image';
import Lozenge from '@atlaskit/lozenge';
import { SkeletonItem } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import {
	useConnectors,
	useConnectorsIsFetching,
	useEnabledKnowledgeSources,
	useNewlyEnabledConnectors,
} from '@atlassian/conversation-assistant-store/controllers/connectors-store';
import { useEnsureConnectorsLoaded } from '@atlassian/conversation-assistant-store/controllers/connectors-store/use-ensure-connectors-loaded';
import { SearchInput } from '@atlassian/conversation-assistant-ui-components/ui/SearchInput';
import { useSideNavModal } from '@atlassian/conversation-assistant-ui-components/ui/side-nav-modal/side-nav-modal-provider';

import { RovoLogoForAppSource } from '../../../../common/ui/RovoLogoForAppSource';
import { useProductSortOrder } from '../../../../common/ui/useProductSortOrder';
import { is3pApp } from '../../../../common/utils/is-3p-app';
import type { RovoApp } from '../../../../controllers/chat-mode-store/types';

import { messages } from './messages';
import { OAuthPopupWrapper } from './oauth-popup-wrapper';
import { getKnowledgeSourceDisplayName } from './utils/get-knowledge-source-display-name';

const MAX_UNCONNECTED_APPS_FOR_DISCOVERY = 10;

const styles = cssMap({
	appList: {
		width: '100%',
		paddingLeft: 0,
		marginLeft: 0,
	},
	appListItem: {
		width: '100%',
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		cursor: 'pointer',
		backgroundColor: token('color.background.neutral.subtle'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	/* When used inside modal row, let parent provide hover to avoid shade mismatch */
	appListItemHoverFromParent: {
		'&:hover': {
			// @ts-expect-error - cssMap hovered background token variant not in union type; required for interactive state
			backgroundColor: token('color.background.neutral.subtle'),
		},
	},
	appSelector: {
		position: 'relative',
		minWidth: '300px',
		width: '100%',
		height: '380px',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		display: 'flex',
		flexDirection: 'column',
	},
	appSelectorModal: {
		position: 'relative',
		minWidth: '300px',
		width: '100%',
		height: '100%',
		minHeight: '0px',
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	scrollableContent: {
		overflowY: 'auto',
		overflowX: 'hidden',
		scrollbarWidth: 'thin',
		flex: 1,
		minHeight: '0px',
	},
	rovoLogoWrapper: {
		height: '24px',
		width: '24px',
		borderRadius: token('radius.small'),
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'center',
		overflow: 'hidden',
		border: `${token('border.width')} solid ${token('color.border')}`,
	},
	placeholderLogo: {
		height: '24px',
		width: '24px',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.accent.blue.subtle'),
	},
	skeletonItem: {
		paddingInline: token('space.0'),
		minHeight: '32px',
	},
	appItemInline: {
		display: 'flex',
	},
	connectButtonText: {
		color: token('color.text.accent.blue'),
		cursor: 'pointer',
		'&:hover': {
			textDecoration: 'underline',
		},
		'&:focus-visible': {
			textDecoration: 'underline',
		},
	},
	appItemContentNew: {
		flex: 1,
	},
	sectionHeader: {
		paddingBlock: token('space.075'),
		paddingInline: token('space.100'),
	},
	searchInputWrapper: {
		flexShrink: 0,
	},
	modalSectionHeader: {
		paddingBlock: token('space.075'),
		paddingInline: token('space.100'),
	},
	modalSectionHeaderWithSearch: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.200'),
		paddingBlock: token('space.075'),
		paddingInlineStart: 0,
	},
	modalSectionList: {
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.small'),
		paddingTop: 0,
		paddingRight: 0,
		paddingBottom: 0,
		paddingLeft: 0,
	},
	/** Grouped app lists in filter popup: no outer border (modal uses modalSectionList) */
	popupSectionList: {
		paddingTop: 0,
		paddingRight: 0,
		paddingBottom: 0,
		paddingLeft: 0,
	},
	modalSectionListItem: {
		marginBlockStart: 0,
		paddingTop: token('space.075'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.075'),
	},
	/* Row hover only for modal content with new UI - not legacy, not popup */
	modalSectionListItemRowHover: {
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	modalSectionListItemDivider: {
		borderBlockEnd: `${token('border.width')} solid ${token('color.border')}`,
	},
	sourceRow: {
		width: '100%',
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		backgroundColor: 'transparent',
	},
	separator: {
		borderBlockEnd: `${token('border.width')} solid ${token('color.border')}`,
		paddingBlockEnd: token('space.100'),
		marginBlockEnd: token('space.100'),
	},
	iconWrapper: {
		width: '24px',
		height: '24px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	viewMoreFooter: {
		flexShrink: 0,
		borderBlockStart: `${token('border.width')} solid ${token('color.border')}`,
		marginBlockStart: token('space.100'),
		paddingBlockStart: token('space.100'),
	},
	connectMoreAppsButton: {
		borderRadius: token('radius.small'),
	},
});

export type LoadedRovoApp = {
	friendlyName: string;
	source: string;
	iconUrl: string;
	isSelected: boolean;
	isUserOAuthed?: boolean;
	outboundAuthUrl?: string;
	isNewlyEnabled?: boolean;
};

type AppSelectorState =
	| { type: 'loading' }
	| {
			type: 'available';
			searchTerm: string | undefined;
			apps: LoadedRovoApp[];
	  }
	| { type: 'errored'; error: Error };

interface AppListItemProps {
	app: LoadedRovoApp;
	onAppSelectionChange: (app: LoadedRovoApp, checked: boolean, successfulOauth?: boolean) => void;
	onAppConnectClick?: (source: string) => void;
	/** Passed to OAuth / analytics to identify where Connect was triggered */
	oauthSource: string;
	/** Which product surface opened the flow (e.g. 'chat', 'search') for analytics attribution */
	oauthSurface?: string;
	/** When true, inner button does not apply hover - parent row provides it for uniform shade */
	suppressItemHover?: boolean;
}

const AppListItem = ({
	app,
	onAppSelectionChange,
	onAppConnectClick,
	oauthSource,
	oauthSurface,
	suppressItemHover,
}: AppListItemProps) => {
	const { sendAnalyticsEvent } = useAnalytics();
	const { formatMessage } = useIntl();
	const handleRowClick = () => {
		const newCheckedState = !app.isSelected;
		if (app.isUserOAuthed) {
			onAppSelectionChange(app, newCheckedState);
		}
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'row',
			actionSubjectId: 'rovoChatFilterByAppsClick',
			attributes: {
				isConnected: app.isUserOAuthed,
				interactionElement: 'row',
				isSelected: newCheckedState,
				appSource: app.source,
				appFriendlyName: app.friendlyName,
				is3pApp: is3pApp(app.friendlyName),
			},
		});
	};

	const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.stopPropagation();
		e.nativeEvent.stopImmediatePropagation();
		onAppSelectionChange(app, e.target.checked);
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'row',
			actionSubjectId: 'rovoChatFilterByAppsClick',
			attributes: {
				isConnected: app.isUserOAuthed,
				interactionElement: 'toggle',
				isSelected: e.target.checked,
				appSource: app.source,
				appFriendlyName: app.friendlyName,
				is3pApp: is3pApp(app.friendlyName),
			},
		});
	};

	const appItemContent = (
		<Pressable
			onClick={handleRowClick}
			xcss={cx(
				styles.appListItem,
				suppressItemHover ? styles.appListItemHoverFromParent : undefined,
			)}
			aria-label={
				!app.isUserOAuthed
					? formatMessage(messages.connectApp, { appName: app.friendlyName })
					: app.isSelected
						? formatMessage(messages.deselectApp, { appName: app.friendlyName })
						: formatMessage(messages.selectApp, { appName: app.friendlyName })
			}
		>
			<Inline space="space.100" key={app.source} as="li" xcss={styles.appItemInline}>
				{fg('rovo_chat_use_be_knowledge_icons') ? (
					<Image
						id={app.source}
						width={24}
						height={24}
						src={
							fg('use_new_knowledge_source_icon_url_prefix')
								? app.iconUrl
								: `/gateway/api${app.iconUrl}`
						}
						alt=""
					/>
				) : (
					<Box xcss={styles.rovoLogoWrapper}>
						<RovoLogoForAppSource
							appSource={app.source}
							rovoLogoProps={{ size: 'small', label: '' }}
							fallback={<Box xcss={styles.placeholderLogo} />}
						/>
					</Box>
				)}
				<Box xcss={styles.appItemContentNew}>
					<Inline grow="fill" spread="space-between" alignBlock="center">
						<Inline space="space.100" alignBlock="center">
							<Text size="medium" weight="medium" maxLines={1}>
								{app.friendlyName}
							</Text>
							{app.isNewlyEnabled && (
								<Lozenge appearance="discovery" isBold>
									{formatMessage(messages.newBadge)}
								</Lozenge>
							)}
						</Inline>
						{app.isUserOAuthed ? (
							<Toggle
								id={app.source}
								isChecked={app.isSelected}
								onChange={handleToggleChange}
								size="regular"
							/>
						) : (
							<Box xcss={styles.connectButtonText} aria-hidden="true">
								<Text size="medium" weight="medium" color="color.link">
									{formatMessage(messages.connect)}
								</Text>
							</Box>
						)}
					</Inline>
				</Box>
			</Inline>
		</Pressable>
	);
	return (
		<OAuthPopupWrapper
			app={app}
			source={oauthSource}
			surface={oauthSurface}
			onAuthorizeAction={() => onAppConnectClick?.(app.source)}
			onCompletedAction={() => {
				onAppSelectionChange(app, true, true);
			}}
			placement="left"
			trigger={(triggerProps) => <div {...triggerProps}>{appItemContent}</div>}
		/>
	);
};

const AppsList = ({
	apps,
	isLoading,
	onAppSelectionChange,
	onAppConnectClick,
	oauthSource,
	oauthSurface,
	showItemDividers = false,
}: {
	apps: LoadedRovoApp[];
	isLoading: boolean;
	onAppSelectionChange: (app: LoadedRovoApp, checked: boolean, successfulOauth?: boolean) => void;
	onAppConnectClick?: (source: string) => void;
	oauthSource: string;
	oauthSurface?: string;
	showItemDividers?: boolean;
}) => {
	if (isLoading) {
		return (
			<Stack as="ul" space="space.100" xcss={styles.appList}>
				{[...Array(7)].map((_, index) => (
					<Box key={index} xcss={styles.skeletonItem}>
						<SkeletonItem hasIcon={true} />
					</Box>
				))}
			</Stack>
		);
	}

	return (
		<Stack as="ul" space="space.0" xcss={styles.appList}>
			{apps.map((app, index) => {
				const isLast = index === apps.length - 1;
				const item = (
					<AppListItem
						key={app.source}
						app={app}
						oauthSource={oauthSource}
						oauthSurface={oauthSurface}
						onAppConnectClick={onAppConnectClick}
						onAppSelectionChange={onAppSelectionChange}
						suppressItemHover={showItemDividers}
					/>
				);
				if (showItemDividers) {
					return (
						<Box
							key={app.source}
							as="li"
							xcss={cx(
								styles.modalSectionListItem,
								!isLast && styles.modalSectionListItemDivider,
								styles.modalSectionListItemRowHover,
							)}
						>
							{item}
						</Box>
					);
				}
				return item;
			})}
		</Stack>
	);
};

export const FilterByAppsSelector = ({
	assistanceServiceParams,
	preservedSelectedApps,
	onChange,
	hideViewMoreButton = false,
	showGroupedSectionsWithCounts = false,
	webSearchEnabled,
	onWebSearchToggle,
	orgKnowledgeEnabled,
	onOrgKnowledgeToggle,
	oauthSource = 'appFilterMenu',
	oauthSurface,
	onAppConnectClick,
	initialScrollToAvailableApps = false,
	enableAppsDiscoveryUi,
}: {
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
	preservedSelectedApps: RovoApp[];
	onChange: (apps: RovoApp[]) => void;
	hideViewMoreButton?: boolean;
	showGroupedSectionsWithCounts?: boolean;
	webSearchEnabled?: boolean;
	onWebSearchToggle?: (enabled: boolean) => void;
	orgKnowledgeEnabled?: boolean;
	onOrgKnowledgeToggle?: (enabled: boolean) => void;
	/** Passed to OAuth when user connects an app; use `rovoSettingsModal` when shown inside the side nav Rovo settings modal */
	oauthSource?: string;
	/** Which product surface opened the flow (e.g. 'chat', 'search') for analytics attribution */
	oauthSurface?: string;
	onAppConnectClick?: (source: string) => void;
	/** When true (e.g. Rovo settings modal opened with scrollToAvailableApps), scroll to the available-apps section once content is ready */
	initialScrollToAvailableApps?: boolean;
	/**
	 * When true, opts this surface into apps discovery UI. The effective UI
	 * still depends on the side nav modal being available.
	 */
	enableAppsDiscoveryUi?: boolean;
}): React.JSX.Element => {
	useEnsureConnectorsLoaded({ assistanceServiceParams });
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const [appSelectorState, setAppSelectorState] = useState<AppSelectorState>({ type: 'loading' });
	const [allSources] = useEnabledKnowledgeSources();
	const [storeFetching] = useConnectorsIsFetching();
	const newlyEnabledConnectors = useNewlyEnabledConnectors();
	const [connectors] = useConnectors();
	const appsAvailableToConnectSectionRef = useRef<HTMLDivElement>(null);
	const hasInitializedRef = useRef(false);
	const newlyEnabledConnectorSet = useMemo(
		() => new Set(newlyEnabledConnectors.map((c) => c.source)),
		[newlyEnabledConnectors],
	);
	const { open: openSideNavModal, disabled: sideNavModalDisabled } = useSideNavModal();
	// Apps discovery UI relies on the side nav modal as the "Discover more apps"
	// destination. When the modal is disabled, keep the in-menu list uncapped.
	const shouldApplyAppsDiscoveryUi = !!enableAppsDiscoveryUi && !sideNavModalDisabled;
	const productSortOrderProvider = useProductSortOrder();

	const scrollToAppsAvailableToConnect = useCallback(() => {
		appsAvailableToConnectSectionRef.current?.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
		});
	}, []);

	const handleConnectMoreClick = useCallback(() => {
		/** Already inside Rovo settings modal — opening again is a no-op; scroll in place */
		if (oauthSource === 'rovoSettingsModal') {
			scrollToAppsAvailableToConnect();
			return;
		}
		if (shouldApplyAppsDiscoveryUi) {
			openSideNavModal({
				title: formatMessage(messages.viewMoreModalTitle),
				searchPlaceholder: formatMessage(messages.searchSettingsPlaceholder),
				navAriaLabel: formatMessage(messages.viewMoreModalTitle),
				defaultSelectedId: 'discovery',
				scrollToAvailableApps: true,
			});
		} else {
			scrollToAppsAvailableToConnect();
		}
	}, [
		oauthSource,
		openSideNavModal,
		formatMessage,
		scrollToAppsAvailableToConnect,
		shouldApplyAppsDiscoveryUi,
	]);

	useEffect(() => {
		if (storeFetching) {
			return;
		}
		if (allSources.length === 0 && appSelectorState.type === 'loading') {
			return;
		}
		if (hasInitializedRef.current) {
			setAppSelectorState((currentState) => {
				if (currentState.type !== 'available') {
					return currentState;
				}
				const selectedSet = new Set(preservedSelectedApps.map((a) => a.source));
				const updatedApps = allSources.map((source) => ({
					friendlyName: getKnowledgeSourceDisplayName(source.source, source.friendlyName),
					source: source.source,
					iconUrl: source.iconUrl,
					isSelected: selectedSet.has(source.source),
					outboundAuthUrl: source.outboundAuthUrl,
					isUserOAuthed: source.isUserOAuthed,
					isNewlyEnabled: newlyEnabledConnectorSet.has(source.source),
				}));
				return { ...currentState, apps: updatedApps };
			});
			return;
		}
		const loadApps = async () => {
			if (hasInitializedRef.current) {
				return;
			}
			const sortProduct = await productSortOrderProvider();
			const shouldSelectAll = preservedSelectedApps.length === 0 && orgKnowledgeEnabled !== false;

			const apps = allSources
				.map((source) => ({
					friendlyName: getKnowledgeSourceDisplayName(source.source, source.friendlyName),
					source: source.source,
					iconUrl: source.iconUrl,
					isSelected: shouldSelectAll
						? !!source.isUserOAuthed
						: preservedSelectedApps.find((app) => app.source === source.source) !== undefined,
					outboundAuthUrl: source.outboundAuthUrl,
					isUserOAuthed: source.isUserOAuthed,
					isNewlyEnabled: newlyEnabledConnectorSet.has(source.source),
				}))
				.sort((a, b) => sortProduct(a.source, b.source));

			setAppSelectorState({
				type: 'available',
				apps,
				searchTerm: undefined,
			});

			if (shouldSelectAll) {
				const selectedApps = apps.filter((app) => app.isSelected);
				onChange(selectedApps);
			}
			hasInitializedRef.current = true;
		};
		loadApps();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		productSortOrderProvider,
		allSources,
		storeFetching,
		preservedSelectedApps,
		appSelectorState.type,
		onChange,
		newlyEnabledConnectorSet,
	]);

	useEffect(() => {
		if (!hasInitializedRef.current) {
			return;
		}
		setAppSelectorState((currentState) => {
			if (currentState.type === 'loading' || currentState.type === 'errored') {
				return currentState;
			}
			return {
				...currentState,
				apps: currentState.apps.map((app) => ({
					...app,
					isSelected: preservedSelectedApps.find((a) => a.source === app.source) !== undefined,
				})),
			};
		});
	}, [preservedSelectedApps, setAppSelectorState]);

	const onAppSelectionChange = useCallback(
		(app: LoadedRovoApp, checked: boolean, successfulOauth?: boolean) => {
			let filteredApps: RovoApp[] = preservedSelectedApps.filter((a) => a.source !== app.source);
			let updatedApp = { ...app };

			if (successfulOauth && appSelectorState.type === 'available') {
				updatedApp = { ...app, isUserOAuthed: true };

				setAppSelectorState((currentState) => {
					if (currentState.type === 'loading' || currentState.type === 'errored') {
						return currentState;
					}
					return {
						...currentState,
						apps: currentState.apps.map((existingApp) =>
							existingApp.source === app.source
								? { ...existingApp, isUserOAuthed: true, isSelected: true }
								: existingApp,
						),
					};
				});
			}

			if (checked) {
				filteredApps = [...filteredApps, updatedApp];
			}
			onChange(filteredApps);
		},
		[preservedSelectedApps, onChange, setAppSelectorState, appSelectorState.type],
	);

	const handleSearchChange = useCallback(
		(searchText: string) => {
			setAppSelectorState((currentState) => {
				if (currentState.type === 'loading' || currentState.type === 'errored') {
					return currentState;
				}
				return {
					...currentState,
					searchTerm: searchText,
				};
			});

			sendAnalyticsEvent({
				action: 'searched',
				actionSubject: 'input',
				actionSubjectId: 'rovoChatFilterByAppsSearchInput',
				attributes: {
					searchValueLength: searchText.length,
				},
			});
		},
		[sendAnalyticsEvent],
	);

	const sortedAndFilteredApps = useMemo(() => {
		if (appSelectorState.type !== 'available') {
			return [];
		}

		return [...appSelectorState.apps].sort((a, b) => {
			const aConnected = a.isUserOAuthed ? 1 : 0;
			const bConnected = b.isUserOAuthed ? 1 : 0;
			if (aConnected !== bConnected) {
				return bConnected - aConnected;
			}
			const aTimestamp = connectors[a.source]?.connectedAt
				? new Date(connectors[a.source].connectedAt!).getTime()
				: 0;
			const bTimestamp = connectors[b.source]?.connectedAt
				? new Date(connectors[b.source].connectedAt!).getTime()
				: 0;
			return bTimestamp - aTimestamp;
		});
	}, [appSelectorState, connectors]);

	const searchTerm =
		appSelectorState.type === 'available' ? appSelectorState.searchTerm : undefined;
	const { connectedApps, availableToConnectApps } = useMemo(() => {
		if (appSelectorState.type !== 'available') {
			return {
				connectedApps: [] as LoadedRovoApp[],
				availableToConnectApps: [] as LoadedRovoApp[],
			};
		}
		let apps = sortedAndFilteredApps;
		if (showGroupedSectionsWithCounts && searchTerm) {
			apps = apps.filter((app) =>
				app.friendlyName.toLowerCase().includes(searchTerm.toLowerCase()),
			);
		}
		const connected = apps.filter((app) => app.isUserOAuthed);
		const available = apps.filter((app) => !app.isUserOAuthed);
		return { connectedApps: connected, availableToConnectApps: available };
	}, [appSelectorState.type, searchTerm, sortedAndFilteredApps, showGroupedSectionsWithCounts]);

	const totalAppsInSections =
		showGroupedSectionsWithCounts && appSelectorState.type === 'available'
			? connectedApps.length + availableToConnectApps.length
			: 0;

	const { connectedAppsForDisplay, availableToConnectAppsForDisplay } = useMemo(() => {
		if (shouldApplyAppsDiscoveryUi) {
			// Apps discovery limit: show all connected apps, cap unconnected apps only
			return {
				connectedAppsForDisplay: connectedApps,
				availableToConnectAppsForDisplay: availableToConnectApps.slice(
					0,
					MAX_UNCONNECTED_APPS_FOR_DISCOVERY,
				),
			};
		}
		return {
			connectedAppsForDisplay: connectedApps,
			availableToConnectAppsForDisplay: availableToConnectApps,
		};
	}, [connectedApps, availableToConnectApps, shouldApplyAppsDiscoveryUi]);

	useEffect(() => {
		if (!initialScrollToAvailableApps) {
			return;
		}
		if (appSelectorState.type !== 'available' || availableToConnectApps.length === 0) {
			return;
		}
		const frame = requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				appsAvailableToConnectSectionRef.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				});
			});
		});
		return () => cancelAnimationFrame(frame);
	}, [initialScrollToAvailableApps, appSelectorState.type, availableToConnectApps.length]);

	const renderContent = () => {
		switch (appSelectorState.type) {
			case 'errored':
				return <></>;
			case 'loading':
			case 'available':
				if (showGroupedSectionsWithCounts && appSelectorState.type === 'available') {
					const isModalGroupedLayout = oauthSource === 'rovoSettingsModal';

					const connectedAppsSummaryRow = (
						<Inline space="space.100" alignBlock="center">
							<Text
								size="small"
								weight={isModalGroupedLayout ? 'bold' : 'semibold'}
								color={isModalGroupedLayout ? 'color.text' : 'color.text.subtlest'}
							>
								{formatMessage(messages.connectedAppsOutOfTotal, {
									connectedCount: connectedApps.length,
									totalCount: totalAppsInSections,
								})}
							</Text>
							{availableToConnectApps.length > 0 && (
								<Pressable
									role="button"
									onClick={handleConnectMoreClick}
									xcss={styles.connectMoreAppsButton}
								>
									<Text size="small" weight="medium" color="color.link">
										{formatMessage(messages.connectMoreApps)}
									</Text>
								</Pressable>
							)}
						</Inline>
					);
					return (
						<Stack space="space.200">
							<Box>
								{isModalGroupedLayout ? (
									<Box xcss={styles.modalSectionHeaderWithSearch}>
										{connectedAppsSummaryRow}
										<Box xcss={styles.searchInputWrapper}>
											<SearchInput
												placeholderText={formatMessage(messages.searchAppsPlaceholder)}
												onSearch={handleSearchChange}
												isDisabled={false}
												debounceTime={200}
												isCompact
											/>
										</Box>
									</Box>
								) : (
									<Box xcss={styles.sectionHeader}>{connectedAppsSummaryRow}</Box>
								)}
								{connectedAppsForDisplay.length > 0 && (
									<Box
										xcss={isModalGroupedLayout ? styles.modalSectionList : styles.popupSectionList}
									>
										<AppsList
											apps={connectedAppsForDisplay}
											onAppSelectionChange={onAppSelectionChange}
											isLoading={false}
											oauthSource={oauthSource}
											oauthSurface={oauthSurface}
											onAppConnectClick={onAppConnectClick}
											showItemDividers={oauthSource === 'rovoSettingsModal'}
										/>
									</Box>
								)}
							</Box>
							{availableToConnectAppsForDisplay.length > 0 && (
								<Box ref={appsAvailableToConnectSectionRef}>
									<Box
										xcss={isModalGroupedLayout ? styles.modalSectionHeader : styles.sectionHeader}
									>
										<Text
											size="small"
											weight={isModalGroupedLayout ? 'bold' : 'semibold'}
											color={isModalGroupedLayout ? 'color.text' : 'color.text.subtlest'}
										>
											{formatMessage(messages.appsAvailableToConnectSectionHeader, {
												count: availableToConnectApps.length,
											})}
										</Text>
									</Box>
									<Box
										xcss={isModalGroupedLayout ? styles.modalSectionList : styles.popupSectionList}
									>
										<AppsList
											apps={availableToConnectAppsForDisplay}
											onAppSelectionChange={onAppSelectionChange}
											isLoading={false}
											oauthSource={oauthSource}
											oauthSurface={oauthSurface}
											onAppConnectClick={onAppConnectClick}
											showItemDividers={oauthSource === 'rovoSettingsModal'}
										/>
									</Box>
								</Box>
							)}
						</Stack>
					);
				}
				return (
					<AppsList
						apps={sortedAndFilteredApps}
						onAppSelectionChange={onAppSelectionChange}
						isLoading={appSelectorState.type === 'loading'}
						oauthSource={oauthSource}
						oauthSurface={oauthSurface}
						onAppConnectClick={onAppConnectClick}
					/>
				);
		}
	};

	const showSourcesSection = onWebSearchToggle !== undefined || onOrgKnowledgeToggle !== undefined;

	return (
		<Box
			xcss={
				showGroupedSectionsWithCounts && oauthSource === 'rovoSettingsModal'
					? styles.appSelectorModal
					: styles.appSelector
			}
		>
			<Box xcss={styles.scrollableContent}>
				{showSourcesSection && (
					<Box xcss={styles.separator}>
						<Box xcss={styles.sectionHeader}>
							<Text size="small" weight="semibold" color="color.text.subtlest">
								{formatMessage(messages.sourcesHeader)}
							</Text>
						</Box>
						<Stack space="space.0">
							{onWebSearchToggle !== undefined && (
								<Pressable
									onClick={(e) => {
										// Only handle clicks on the row itself, not on the Toggle (which handles its own events)
										// This was causing a double state change on the toggle.
										if (e.target instanceof HTMLInputElement) {
											return;
										}
										onWebSearchToggle(!webSearchEnabled);
									}}
									xcss={styles.sourceRow}
								>
									<Inline space="space.100" alignBlock="center">
										<Box xcss={styles.iconWrapper}>
											<GlobeIcon label="" size="medium" />
										</Box>
										<Box xcss={styles.appItemContentNew}>
											<Inline grow="fill" spread="space-between" alignBlock="center">
												<Text size="medium" weight="medium">
													{formatMessage(messages.includeWebResults)}
												</Text>
												<Toggle
													id="web_search_toggle"
													isChecked={!!webSearchEnabled}
													onChange={(e) => {
														e.stopPropagation();
														onWebSearchToggle(e.target.checked);
													}}
													size="regular"
												/>
											</Inline>
										</Box>
									</Inline>
								</Pressable>
							)}
							{onOrgKnowledgeToggle !== undefined && (
								<Pressable
									onClick={(e) => {
										// Only handle clicks on the row itself, not on the Toggle (which handles its own events)
										// This was causing a double state change on the toggle.
										if (e.target instanceof HTMLInputElement) {
											return;
										}
										onOrgKnowledgeToggle(!orgKnowledgeEnabled);
									}}
									xcss={styles.sourceRow}
								>
									<Inline space="space.100" alignBlock="center">
										<Box xcss={styles.iconWrapper}>
											<OfficeBuildingIcon label="" size="medium" />
										</Box>
										<Box xcss={styles.appItemContentNew}>
											<Inline grow="fill" spread="space-between" alignBlock="center">
												<Text size="medium" weight="medium">
													{formatMessage(messages.searchCompanyKnowledge)}
												</Text>
												<Toggle
													id="org_knowledge_toggle"
													isChecked={!!orgKnowledgeEnabled}
													onChange={(e) => {
														e.stopPropagation();
														onOrgKnowledgeToggle(e.target.checked);
													}}
													size="regular"
												/>
											</Inline>
										</Box>
									</Inline>
								</Pressable>
							)}
						</Stack>
					</Box>
				)}
				{!showGroupedSectionsWithCounts && (
					<Box xcss={styles.sectionHeader}>
						<Text size="small" weight="semibold" color="color.text.subtlest">
							{formatMessage(messages.appsHeader)}
						</Text>
					</Box>
				)}
				{renderContent()}
			</Box>
			{!hideViewMoreButton && shouldApplyAppsDiscoveryUi && (
				<Box xcss={styles.viewMoreFooter}>
					<Pressable
						xcss={styles.sourceRow}
						onClick={() => {
							openSideNavModal({
								title: formatMessage(messages.viewMoreModalTitle),
								searchPlaceholder: formatMessage(messages.searchSettingsPlaceholder),
								navAriaLabel: formatMessage(messages.viewMoreModalTitle),
								defaultSelectedId: 'discovery',
							});
						}}
					>
						<Inline space="space.100" alignBlock="center">
							<Box xcss={styles.iconWrapper}>
								<AppsIcon label="" size="medium" />
							</Box>
							<Text size="medium" weight="medium">
								{formatMessage(messages.discoverMoreApps)}
							</Text>
						</Inline>
					</Pressable>
				</Box>
			)}
		</Box>
	);
};
